import { Button, Stack } from '@mui/material';
import { MprcBoxDrawer, MprcContainer, MprcFileSelection, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { AsyncUploadResult, FileGetDto } from 'generated/CoreApi_v1';
import MediaUtils from 'lib/MediaUtils';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useEffect, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone/.';
import { useTranslation } from 'react-i18next';

interface IProps {
    enrollmentId: string;
    componentId: string;
    maxFileSize?: number | null;
    onClose: (reload?: boolean) => void;
}

export const ActivityDeliveryDrawer = ({ enrollmentId, componentId, maxFileSize, onClose }: IProps) => {
    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enqueueSnackbar } = useSnackbar();
    const [uploading, setUploading] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [file, setFile] = useState<FileGetDto | null>(null);

    const dropAcceptFiles: Accept = useMemo(() => {
        var files: Accept = {};
        files["image/*"] = MediaUtils.AcceptImages();
        files["audio/*"] = MediaUtils.AcceptAudios();
        files["video/*"] = MediaUtils.AcceptVideos();

        for (var docType of MediaUtils.AcceptDocuments())
            files[`application/${docType.replace(".", "")}`] = [docType];

        return files;
    }, []);

    ///
    const handleUploadSuccess = (uploadResult: AsyncUploadResult) => {
        if (uploadResult == null) {
            enqueueSnackbar(t("Student.EnrollmentView.DeliveryActivityDrawer.UploadFailed"), { variant: "error" });
        }
        else {
            setFile(uploadResult.mainFile);
        }
        setUploading(false);
    };

    const handleUploadError = (error: any) => {
        enqueueSnackbar(error.message ?? error, { variant: "error" });
    };

    const reset = () => {
        setFile(null);
        setViewError(null);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function save() {
            try {
                var result = await api.v1.enrollment.deliveryActivity({ enrollmentId, componentId, fileId: file!.fileId });
                if (result) {
                    enqueueSnackbar(t("Student.EnrollmentView.DeliveryActivityDrawer.Success"), { variant: "success" });
                    onClose(true);
                }
                else {
                    enqueueSnackbar(t("Student.EnrollmentView.DeliveryActivityDrawer.Failed"), { variant: "error" });
                }
            }
            catch (e: any) {
                setViewError(e);
            }
            finally {
                setSaving(false);
            }
        }
        if (saving) save();
    }, [saving]);

    ///
    return (
        <>
            <MprcBoxDrawer
                open
                title={t("Student.EnrollmentView.DeliveryActivityDrawer.Title")}
                onClose={(reason) => reason == "exit" && onClose(false)}
            >
                <MprcRequestIndicator visible={saving} type="post" />

                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />

                <MprcContainer>
                    <MprcFileSelection
                        acceptedFiles={dropAcceptFiles}
                        fileNotAcceptText={t("Student.EnrollmentView.DeliveryActivityDrawer.FileNotAcceptMessage")}
                        maxSize={maxFileSize ? maxFileSize * 1048576 : undefined}
                        selectedFile={file}
                        onFileUploadStarted={() => setUploading(true)}
                        onFileUploadFinished={() => setUploading(false)}
                        onFileUploadError={handleUploadError}
                        onFileUploadSuccess={handleUploadSuccess}
                        onClear={() => reset()}
                        uploadMessage={[
                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL1"),
                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL2"),
                            t("Student.EnrollmentView.DeliveryActivityDrawer.MessageL3"),
                        ]}
                    />
                </MprcContainer>

                <Stack justifyContent="center" alignItems="center" direction="row">
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => setSaving(true)}
                        disabled={uploading || file == null}
                    >
                        {t("Student.EnrollmentView.DeliveryActivityDrawer.DeliveryButttonText")}
                    </Button>
                </Stack>

            </MprcBoxDrawer>
        </>
    );
};
